import { useTranslation } from 'react-i18next';

import { Input, InputField, Select } from '@saas-kit/ui';

import { DisplayLogoField } from './DisplayLogoField';
import { DisplayMediaField } from './DisplayMediaField';
import type { DisplayFormState } from './displayFormState';
import type {
  QmsCounter,
  QmsCounterGroup,
  QmsDisplayKind,
  QmsLabelSize,
} from './types';

/** Layout, heading, and branding controls of the display setup form. */
export function DisplayLayoutFields({
  form,
  groups,
  counters,
  onChange,
}: {
  form: DisplayFormState;
  groups: QmsCounterGroup[];
  counters: QmsCounter[];
  onChange: (patch: Partial<DisplayFormState>) => void;
}): JSX.Element {
  const { t } = useTranslation('qms');

  return (
    <>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block space-y-1 text-sm">
          <span className="text-foreground">{t('displays.kind')}</span>
          <Select
            value={form.kind}
            onChange={(e) => onChange({ kind: e.target.value as QmsDisplayKind })}
            data-testid="display-kind"
          >
            <option value="group">{t('displays.kind_group')}</option>
            <option value="all">{t('displays.kind_all')}</option>
            <option value="counter">{t('displays.kind_counter')}</option>
          </Select>
        </label>
        {form.kind === 'group' ? (
          <label className="block space-y-1 text-sm">
            <span className="text-foreground">{t('displays.target_group')}</span>
            <Select
              value={form.group_id}
              onChange={(e) => onChange({ group_id: e.target.value })}
              required
            >
              <option value="">{t('fields.none')}</option>
              {groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </Select>
          </label>
        ) : null}
        {form.kind === 'counter' ? (
          <label className="block space-y-1 text-sm">
            <span className="text-foreground">
              {t('displays.target_counter')}
            </span>
            <Select
              value={form.counter_id}
              onChange={(e) => onChange({ counter_id: e.target.value })}
              required
            >
              <option value="">{t('fields.none')}</option>
              {counters.map((counter) => (
                <option key={counter.id} value={counter.id}>
                  {counter.name}
                </option>
              ))}
            </Select>
          </label>
        ) : null}
        {form.kind === 'all' ? (
          <p className="self-end text-xs text-muted-foreground">
            {t('displays.kind_all_hint')}
          </p>
        ) : null}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <InputField
          label={t('displays.form.heading')}
          hint={t('displays.form.heading_hint')}
          value={form.title}
          onChange={(e) => onChange({ title: e.target.value })}
        />
        <InputField
          label={t('displays.form.heading_ar')}
          hint={t('displays.form.heading_ar_hint')}
          value={form.title_ar}
          dir="rtl"
          onChange={(e) => onChange({ title_ar: e.target.value })}
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {form.kind !== 'counter' ? (
          <DisplayLogoField
            value={form.logo_file_id}
            onChange={(logo_file_id) => onChange({ logo_file_id })}
          />
        ) : null}
        <label className="block space-y-1 text-sm">
          <span className="text-foreground">
            {t('displays.form.accent_color')}
          </span>
          <Input
            type="color"
            value={form.accent_color || '#1d4ed8'}
            onChange={(e) => onChange({ accent_color: e.target.value })}
            className="h-9 w-20 p-1"
            data-testid="display-accent"
          />
          <span className="block text-xs text-muted-foreground">
            {t('displays.form.accent_color_hint')}
          </span>
        </label>
        {form.kind !== 'counter' ? (
          <label className="block space-y-1 text-sm">
            <span className="text-foreground">
              {t('displays.form.label_color')}
            </span>
            <Input
              type="color"
              value={form.label_color || form.accent_color || '#1d4ed8'}
              onChange={(e) => onChange({ label_color: e.target.value })}
              className="h-9 w-20 p-1"
              data-testid="display-label-color"
            />
            <span className="block text-xs text-muted-foreground">
              {t('displays.form.label_color_hint')}
            </span>
          </label>
        ) : null}
        {form.kind !== 'counter' ? (
          <label className="block space-y-1 text-sm">
            <span className="text-foreground">
              {t('displays.form.label_size')}
            </span>
            <Select
              value={form.label_size}
              onChange={(e) =>
                onChange({ label_size: e.target.value as QmsLabelSize })
              }
              data-testid="display-label-size"
            >
              <option value="sm">{t('displays.form.label_size_sm')}</option>
              <option value="md">{t('displays.form.label_size_md')}</option>
              <option value="lg">{t('displays.form.label_size_lg')}</option>
              <option value="xl">{t('displays.form.label_size_xl')}</option>
            </Select>
            <span className="block text-xs text-muted-foreground">
              {t('displays.form.label_size_hint')}
            </span>
          </label>
        ) : null}
      </div>

      {form.kind !== 'counter' ? (
        <>
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={form.show_clock}
              onChange={(e) => onChange({ show_clock: e.target.checked })}
              data-testid="display-show-clock"
            />
            {t('displays.form.show_clock')}
          </label>
          <DisplayMediaField
            value={form.branding}
            onChange={(branding) => onChange({ branding })}
          />
        </>
      ) : null}
    </>
  );
}
